import {Dialog, DialogPanel, DialogTitle} from "@headlessui/react";
import {XMarkIcon} from "@heroicons/react/24/outline";
import React, {useEffect, useState} from "react";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
} from 'chart.js';

import "react-datepicker/dist/react-datepicker.css";
import {decodeLoggedAt, formatMoney, toDateTime} from "../utils/utils";
import classNames from "classnames";
import {formatDate} from "date-fns";
import {SubmitButton} from "./Button";
import ExpenseDeleteConfirmation from "./ExpenseDeleteConfirmation";


// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
);



export const ExepenseUpdateSidebar = ({expense, controls, isOpen, setIsOpen}) => {

    console.log('ExepenseUpdateSidebar expense:', expense)
    console.log('ExepenseUpdateSidebar controls:', controls)
    console.log('ExepenseUpdateSidebar isOpen:', isOpen)

    const sortedEntriesDesc = React.useMemo(() => 
        expense?.entries?.sort((a,b) => decodeLoggedAt(b.loggedAt) - decodeLoggedAt(a.loggedAt)) || [], 
        [expense?.entries]
    )
    const [newExpenseCost, setNewExpenseCost] = useState(!expense ? 0 : sortedEntriesDesc[0].costInCents / 100)
    const [month, setMonth] = useState(new Date().getMonth())
    const [year, setYear] = useState(new Date().getFullYear())
    const [confirmDeleteEntryTimestamp, setConfirmDeleteEntryTimestamp] = useState(null)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)

    const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i)

    // Prepare chart data for expense history
    const chartData = React.useMemo(() => {
        if (!expense?.entries || expense.entries.length === 0) {
            return null;
        }

        // Sort entries by date (oldest first for chart)
        const sortedEntries = [...expense.entries].sort((a, b) => 
            decodeLoggedAt(a.loggedAt) - decodeLoggedAt(b.loggedAt)
        );

        const labels = sortedEntries.map(entry => 
            formatDate(toDateTime(decodeLoggedAt(entry.loggedAt)), 'MMM yyyy')
        );
        
        const data = sortedEntries.map(entry => entry.costInCents / 100);

        return {
            labels,
            datasets: [
                {
                    label: 'Expense Amount',
                    data,
                    borderColor: 'rgb(99, 102, 241)', // indigo-500
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    borderWidth: 2,
                    pointBackgroundColor: 'rgb(99, 102, 241)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    tension: 0.1,
                    fill: true,
                },
            ],
        };
    }, [expense?.entries]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `Amount: ${formatMoney(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Date'
                },
                grid: {
                    display: false
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Amount ($)'
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return '$' + value.toLocaleString();
                    }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    useEffect(() => {
        setNewExpenseCost(!expense ? 0 : sortedEntriesDesc[0].costInCents / 100)
    }, [expense, sortedEntriesDesc])

    if (!expense) {
        return null
    }


    return <Dialog open={isOpen} onClose={setIsOpen} className="relative z-50">
        <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <DialogPanel
                    transition
                    className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                >
                    <form className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                        <div className="h-0 flex-1 overflow-y-auto">
                            <div className="bg-indigo-700 px-4 py-6 sm:px-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <DialogTitle className="text-base font-semibold leading-6 text-white">{expense.name}</DialogTitle>
                                        <div className="mt-2">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                expense.expenseType === 'recurring' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {expense.expenseType === 'recurring' ? 'Recurring' : 'Variable'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-3 flex h-7 items-center">
                                        <button
                                            type="button"
                                            onClick={() => setIsOpen(false)}
                                            className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                        >
                                            <span className="absolute -inset-2.5" />
                                            <span className="sr-only">Close panel</span>
                                            <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-1 flex-col justify-between">
                                <div className="divide-y divide-gray-200 px-4 sm:px-6">
                                    <div className="space-y-6 pb-5 pt-6">
                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                                                How much you spend monthly on it?
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    id="value"
                                                    name="value"
                                                    type="number"
                                                    value={newExpenseCost}
                                                    onChange={(e) => setNewExpenseCost(e.target.value)}
                                                    rows={4}
                                                    className="ddblock w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                    defaultValue={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-1 flex-col justify-between">
                                <div className="divide-y pb-6 divide-gray-200 px-4 sm:px-6">
                                    <div className="space-y-6 pb-5 pt-6">
                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                                                Month / Year
                                            </label>
                                            <div className="mt-2 flex">
                                                <select
                                                    id="location"
                                                    name="location"
                                                    value={month}
                                                    onChange={(e) => setMonth(e.target.value)}
                                                    className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                >
                                                    <option value={0}>01</option>
                                                    <option value={1}>02</option>
                                                    <option value={2}>03</option>
                                                    <option value={3}>04</option>
                                                    <option value={4}>05</option>
                                                    <option value={5}>06</option>
                                                    <option value={6}>07</option>
                                                    <option value={7}>08</option>
                                                    <option value={8}>09</option>
                                                    <option value={9}>10</option>
                                                    <option value={10}>11</option>
                                                    <option value={11}>12</option>
                                                </select>

                                                <div style={{"padding-top": "0.7rem"}}>
                                                    &nbsp;/&nbsp;
                                                </div>

                                                <select
                                                    id="location"
                                                    name="location"
                                                    value={year}
                                                    onChange={(e) => {
                                                        setYear(e.target.value)
                                                    }}
                                                    className="mt-2 min-w-1 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                >
                                                    {years.map((year) => (
                                                        <option value={year}>{year}</option>
                                                    ))}
                                                </select>

                                                {/*<DatePicker*/}
                                                {/*    className="ddblock w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"*/}
                                                {/*    selected={loggedAt.startDate}*/}
                                                {/*    onChange={(date) => handleValueChange({startDate: date})}*/}
                                                {/*/>*/}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className="flex flex-1 flex-col justify-between">
                                <div className="divide-gray-200 px-4 sm:px-6">
                                    <div className="mt-5 mb-3 text-base font-semibold leading-6 text-gray-900">History</div>
                                    
                                    {/* Expense History Chart */}
                                    {chartData && expense.entries.length > 1 && (
                                        <div className="mb-6">
                                            <div className="h-48 w-full">
                                                <Line data={chartData} options={chartOptions} />
                                            </div>
                                        </div>
                                    )}
                                    
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                        Month/Year
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Amount
                                    </th>
                                    {expense.entries.length > 1 && <th scope="col" className="py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Actions
                                    </th>}

                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {expense.entries.map((entry) => (
                                    <tr key={decodeLoggedAt(entry.loggedAt)}>
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                            {formatDate(toDateTime(decodeLoggedAt(entry.loggedAt)), 'MM/yyyy')}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatMoney(entry.costInCents/100)}</td>
                                        <td className="whitespace-nowrap py-4 text-sm text-gray-500">
                                            {expense.entries.length > 1 && (confirmDeleteEntryTimestamp === entry.uuid ? (
                                                <button
                                                    onClick={async () => {
                                                        await controls.removeEntry({
                                                            expenseId: expense.id,
                                                            uuid: entry.uuid,
                                                        })
                                                        setIsOpen(false)
                                                        await controls.selectExpense(null)
                                                    }}
                                                    type="button"
                                                    className={classNames(
                                                        "rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-inset",
                                                        "bg-red-500 text-red-50 ring-red-300 hover:bg-red-500 "
                                                    )}
                                                >
                                                    Confirm
                                                </button>) :
                                            <button
                                                onClick={() => {
                                                    setConfirmDeleteEntryTimestamp(entry.uuid)
                                                }}
                                                type="button"
                                                className={classNames(
                                                    "rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-inset",
                                                    " bg-red text-red-900 ring-red-300 hover:bg-red-50 "
                                                )}
                                            >
                                                &nbsp;Delete&nbsp;&nbsp;
                                            </button>)}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            
                            <div className="mt-6">
                                <button className={"text-red-700 font-medium underline"} onClick={() => setIsDeleteOpen(true)}>Want to delete this expense category?</button>
                            </div>
                            </div>
                            </div>
                        </div>
                        <div className="flex flex-shrink-0 justify-end px-4 py-4">
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <SubmitButton
                                text={"Save"}
                                loadingText={"Saving..."}
                                onSubmit={async () => {
                                    await controls.save({costInCents: newExpenseCost * 100, loggedAt: new Date(year, month, 14)})
                                }}
                                />
                        </div>
                    </form>
                </DialogPanel>
            </div>
        </div>
    </div>
    <ExpenseDeleteConfirmation isOpen={isDeleteOpen} setIsOpen={setIsDeleteOpen} controls={controls} expense={expense} />
    </Dialog>
}
